import React, { createContext, useCallback, useContext, useLayoutEffect, useState } from 'react'
import Box from '@mui/material/Box'
import ButtonBase from '@mui/material/ButtonBase'
import Typography from '@mui/material/Typography'
import ArrowBack from '@mui/icons-material/ArrowBack'
import { makeStyles } from '@mui/styles'
import classnames from 'classnames'
import { useNavigate } from 'react-router-dom'
import { useIsOneClick } from '@/dashboard/dashboardTheme'

const useStyles = makeStyles(() => ({
  // One-click nav's way back to the splash launcher, trailing the page's section
  // tabs. It wears the Work Order "Intelligence" yellow (soft face, deeper base
  // edge) so it stands apart from the grey tabs beside it.
  button: {
    flexShrink: 0,
    alignSelf: 'center',
    height: 52,
    boxSizing: 'border-box',
    marginLeft: 16,
    borderRadius: 8,
    padding: '0 24px 0 18px',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 10,
    fontFamily: 'Roboto',
    fontSize: 18,
    fontWeight: 700,
    whiteSpace: 'nowrap',
    color: '#2A2118',
    backgroundColor: '#FFF0B3',
    border: '1px solid #F2D264',
    borderBottom: '3px solid #E0B62E',
    transition: 'background-color 120ms',
    '&:hover': { backgroundColor: '#FFE28A' },
    '&.Mui-focusVisible': { outline: '2px solid #B8862A', outlineOffset: 2 },
    '& .MuiSvgIcon-root': { fontSize: 24 },
    '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
  },
  // Leading the strip (One Click's tab rows), the gap sits after it instead.
  leading: {
    marginLeft: 0,
    marginRight: 16,
  },
  // A page with no tab row gets the button on a slim row of its own at the top.
  standaloneRow: {
    display: 'flex',
    padding: '12px 24px 0',
    '& $button': { marginLeft: 0 },
  },
  // One Click shows only the page the tile opened, styled as that tab would be
  // when selected, so the strip never lists the section's other pages.
  currentTab: {
    fontFamily: 'Roboto',
    fontSize: 20,
    fontWeight: 500,
    color: '#4A3D9E',
    padding: '12px',
    whiteSpace: 'nowrap',
  },
}))

// The tab rows' buttons register here while mounted, so the layout knows
// whether the page already has one and only adds the standalone row when not.
const TabRowButtonContext = createContext<((delta: number) => void) | null>(null)

/** Wraps the page content; renders the standalone row for pages without tabs. */
export const BackToHomeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [inTabRows, setInTabRows] = useState(0)
  const register = useCallback((delta: number) => setInTabRows((count) => count + delta), [])
  return (
    <TabRowButtonContext.Provider value={register}>
      {inTabRows === 0 && <StandaloneBackToHome />}
      {children}
    </TabRowButtonContext.Provider>
  )
}

const StandaloneBackToHome: React.FC = () => {
  const classes = useStyles()
  const isOneClick = useIsOneClick()
  if (!isOneClick) return null
  return (
    <Box className={classes.standaloneRow}>
      <HomeButton />
    </Box>
  )
}

const BACK_ICON = <ArrowBack />

const HomeButton: React.FC<{ leading?: boolean }> = ({ leading }) => {
  const classes = useStyles()
  const navigate = useNavigate()
  const goHome = useCallback(() => navigate('/'), [navigate])
  return (
    <ButtonBase className={classnames(classes.button, leading && classes.leading)} onClick={goHome}>
      {BACK_ICON}
      Back to Home
    </ButtonBase>
  )
}

/**
 * The tab rows' button. Renders only in One Click view, where there is no category
 * sidenav. `leading` places it first on the row (before the current tab's label).
 */
const BackToHomeButton: React.FC<{ leading?: boolean }> = ({ leading }) => {
  const isOneClick = useIsOneClick()
  const register = useContext(TabRowButtonContext)
  // Layout effect, so the standalone row is gone before the page paints.
  useLayoutEffect(() => {
    register?.(1)
    return () => register?.(-1)
  }, [register])
  return isOneClick ? <HomeButton leading={leading} /> : null
}

/** One Click's stand-in for the tab strip: the label of the tab the tile opened. */
export const CurrentTabLabel: React.FC<{ label: string }> = ({ label }) => {
  const classes = useStyles()
  return (
    <Typography className={classes.currentTab} component="h2">
      {label}
    </Typography>
  )
}

export default BackToHomeButton
